import React, { useEffect, useState, useCallback } from "react";
import { useBluelagoon } from "@bluelagoon";
import { Card, CardContent, CardHeader, CardTitle } from "@shadcn-in-nx/ui/card";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Button } from "@shadcn-in-nx/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shadcn-in-nx/ui/table";
import { Loader2, ChevronLeft, ChevronRight, History, RefreshCw } from "lucide-react";
import { format } from "date-fns";

export default function CreditLogs({ companyId }) {
    const [data, setData] = useState(null);
    const [page, setPage] = useState(1);
    const { call: fetchDetails, isLoading } = useBluelagoon({ method: "getCompanyDetails" });

    const loadLogs = useCallback(() => {
        if (!companyId) return;
        fetchDetails({ companyId, logsPage: page, logsLimit: 15 }).then(response => {
            if (response?.data) setData(response.data);
        }).catch(err => {
            console.error("Failed to fetch credit logs:", err);
        });
    }, [companyId, page, fetchDetails]);

    useEffect(() => {
        loadLogs();
    }, [loadLogs]);

    if (!companyId) return null;

    const logs = data?.creditLogs || [];
    const pagination = data?.logsPagination;

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base">
                    <History className="h-5 w-5" />
                    Credit Logs {data?.name && `- ${data.name}`}
                </CardTitle>
                <Button variant="ghost" size="icon" onClick={loadLogs} disabled={isLoading}>
                    <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
                </Button>
            </CardHeader>
            <CardContent>
                {isLoading && !data ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                ) : (
                    <>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead className="text-right">Change</TableHead>
                                    <TableHead className="text-right">Balance</TableHead>
                                    <TableHead>Description</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {logs.map((log) => (
                                    <TableRow key={log.id}>
                                        <TableCell className="text-xs whitespace-nowrap">
                                            {format(new Date(log.date), "MMM d, yyyy HH:mm")}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className="text-xs">{log.type || "USAGE"}</Badge>
                                        </TableCell>
                                        <TableCell className={`text-right font-mono text-sm ${log.change >= 0 ? "text-green-600" : "text-red-600"}`}>
                                            {log.change >= 0 ? "+" : ""}{log.change.toFixed(2)}
                                        </TableCell>
                                        <TableCell className="text-right font-mono text-sm">
                                            {log.creditsAfter.toFixed(2)}
                                        </TableCell>
                                        <TableCell className="text-xs max-w-[300px] truncate" title={log.description}>
                                            {log.description || "-"}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {!logs.length && (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                                            No credit logs found
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        {pagination && (page > 1 || pagination.hasMore) && (
                            <div className="flex items-center justify-between mt-4 pt-4 border-t">
                                <p className="text-sm text-muted-foreground">
                                    Page {pagination.page} (last 90 days)
                                </p>
                                <div className="flex gap-2">
                                    <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page <= 1 || isLoading}>
                                        <ChevronLeft className="h-4 w-4" />
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={!pagination.hasMore || isLoading}>
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}
